import { PlotData } from "plotly.js";
import { DefectMetrics } from "../types/Metric";
import { DefectCode } from "../store/slices/defectSlice";

const defectPlotDefaults: Partial<PlotData> = {
    type: "bar",
    hovertemplate: '%{x}: %{y}',
    orientation: "h",
    textfont: {
        color: "white",
        size: 14
    },
    textposition: "inside",
    insidetextanchor: "middle"
}

export const mapDefectsMetrics = (
    metrics: DefectMetrics[] | undefined,
    customHoverTemplate?: (metric: DefectMetrics) => string
) => {
    if (!metrics?.length) {
        return [];
    }

    const xValues: number[] = [];
    const yValues: string[] = [];
    const hoverValues: string[] = [];
    const typeCodeValues: string[] = [];

    metrics.forEach((metric) => {
        const { display_name, type_code, num_occurrences } = metric;
        const displayName = display_name ?? type_code;
        
        typeCodeValues.push(type_code);
        xValues.push(num_occurrences);
        yValues.push(displayName?.length > 40 ? displayName.substring(0, 40) + '...' : displayName);
        hoverValues.push(
            customHoverTemplate
                ? customHoverTemplate(metric)
                : `<b>${displayName}</b><br>Code: ${type_code}<br>—<br>Occurrences: %{x}`
        );
    });

    const mappedMostCommonDefects = {
        ...defectPlotDefaults,
        x: xValues,
        y: yValues,
        customdata: typeCodeValues,
        marker: {
            color: "#28235B"
        },
        name: "All",
        text: xValues.map(String),
        hovertemplate: hoverValues
    };

    return [mappedMostCommonDefects];
};

/**
 * Sorts and transforms recurring defect metrics by total occurrences.
 * Calculates total occurrences (num_occurrences * vessel count) and preserves
 * the original per-vessel count for hover templates.
 * 
 * @param metrics - Array of defect metrics to process
 * @returns Sorted and transformed metrics with total occurrences
 */
export const prepareRecurringDefectMetrics = (
    metrics: DefectMetrics[]
): DefectMetrics[] => {
    // Sort by total occurrences (num_occurrences * vessel count)
    const sortedMetrics = [...metrics].sort((a, b) => {
        const totalA = a.num_occurrences || 0;
        const totalB = b.num_occurrences || 0;
        return totalB - totalA;
    });

    return sortedMetrics.map(metric => ({
        ...metric,
        num_occurrences: metric.num_occurrences || 0,
        per_vessel_occurrences: metric.num_occurrences, // Preserve original for hover
    }));
};

export const buildRecurringDefectHoverTemplate = (
    metric: DefectMetrics,
    defectCodes: DefectCode[]
): string => {
    const displayName = metric.display_name ?? metric.type_code;
    const vesselCount = metric.imo_numbers?.length || 0;
    const matchingCode = defectCodes.find((code) => code.code === metric.type_code);
    const category = matchingCode?.category;

    let template = `<b>${displayName}</b>`;
    
    if (category) {
        template += `<br>Area: ${category}`;
    }
    
    template += `<br>Code: ${metric.type_code}<br>—<br>Occurrences: %{x}<br>Vessels: ${vesselCount}`;
    
    return template;
};